import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/cn';
import type { League } from '../services/leagueService';

const LOCKED_ICON = '/locked.webp';

// Get local league icon path based on rank_level
const getLocalLeagueIcon = (rankLevel: number): string => {
    return `/leagues/${rankLevel}.webp`;
};

// Preload league icons
const preloadLeagueIcons = (leagues: League[]) => {
    leagues.forEach(league => {
        const img = new Image();
        img.src = getLocalLeagueIcon(league.rank_level);
    });
    // Also preload locked icon
    const lockedImg = new Image();
    lockedImg.src = LOCKED_ICON;
};

interface LeagueTierListProps {
    leagues: League[];
    currentRankLevel: number;
}

const getLeagueColor = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('bronze') || n.includes('برنز')) return { bg: 'bg-amber-600', border: 'border-amber-700', shadow: 'shadow-amber-900/20', gradient: 'from-amber-400 to-amber-600' };
    if (n.includes('silver') || n.includes('نقره')) return { bg: 'bg-slate-400', border: 'border-slate-500', shadow: 'shadow-slate-900/20', gradient: 'from-slate-300 to-slate-500' };
    if (n.includes('gold') || n.includes('طلا')) return { bg: 'bg-yellow-500', border: 'border-yellow-600', shadow: 'shadow-yellow-900/20', gradient: 'from-yellow-300 to-yellow-500' };
    if (n.includes('platinum') || n.includes('پلاتین')) return { bg: 'bg-cyan-500', border: 'border-cyan-600', shadow: 'shadow-cyan-900/20', gradient: 'from-cyan-300 to-cyan-500' };
    if (n.includes('diamond') || n.includes('الماس')) return { bg: 'bg-purple-500', border: 'border-purple-600', shadow: 'shadow-purple-900/20', gradient: 'from-purple-300 to-purple-500' };
    if (n.includes('master') || n.includes('استاد')) return { bg: 'bg-red-500', border: 'border-red-600', shadow: 'shadow-red-900/20', gradient: 'from-red-400 to-red-600' };
    if (n.includes('grand') || n.includes('بزرگ')) return { bg: 'bg-slate-900', border: 'border-yellow-500', shadow: 'shadow-black/40', gradient: 'from-slate-800 to-slate-950', text: 'text-yellow-400' };

    return { bg: 'bg-primary-500', border: 'border-primary-600', shadow: 'shadow-primary-900/20', gradient: 'from-primary-400 to-primary-600' };
};

// Check if icon is a valid URL
const isValidIconUrl = (icon: string | null): boolean => {
    if (!icon || icon.trim() === '') return false;
    return icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('/uploads/') || icon.startsWith('uploads/');
};

// League Icon component that handles uploaded URL first, then local default icon
const LeagueIcon: React.FC<{ icon: string | null; rankLevel: number; name: string; className?: string }> = ({ icon, rankLevel, name, className }) => {
    const [localError, setLocalError] = useState(false);
    const [urlError, setUrlError] = useState(false);

    const localIconPath = getLocalLeagueIcon(rankLevel);
    const hasValidUrl = isValidIconUrl(icon);

    React.useEffect(() => {
        setLocalError(false);
        setUrlError(false);
    }, [icon, rankLevel]);

    // Uploaded icon has priority. If it fails, use the default local icon.
    if (hasValidUrl && !urlError) {
        return (
            <img
                src={icon!}
                alt={name}
                className={cn("object-cover filter drop-shadow-lg", className)}
                onError={() => setUrlError(true)}
            />
        );
    }

    if (!localError) {
        return (
            <img
                src={localIconPath}
                alt={name}
                className={cn("object-contain filter drop-shadow-lg", className)}
                onError={() => setLocalError(true)}
            />
        );
    }

    // Both failed, show locked icon
    return (
        <img
            src={LOCKED_ICON}
            alt={name}
            className={cn("object-contain filter drop-shadow-lg", className)}
        />
    );
};

export const LeagueTierList: React.FC<LeagueTierListProps> = ({ leagues, currentRankLevel }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const currentLeagueRef = useRef<HTMLDivElement>(null);
    const [imagesLoaded, setImagesLoaded] = useState(false);

    // Show all leagues sorted by rank_level (descending for RTL - highest first)
    const displayLeagues = [...leagues].sort((a, b) => b.rank_level - a.rank_level);

    // Preload images on mount
    useEffect(() => {
        if (leagues.length > 0) {
            preloadLeagueIcons(leagues);
            // Give images a moment to load
            const timer = setTimeout(() => setImagesLoaded(true), 100);
            return () => clearTimeout(timer);
        }
    }, [leagues]);

    // Auto-scroll to current league after images are loaded
    useEffect(() => {
        if (imagesLoaded && currentLeagueRef.current && scrollRef.current) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                currentLeagueRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            });
        }
    }, [imagesLoaded, currentRankLevel]);

    return (
        <div
            ref={scrollRef}
            className={cn(
                "flex gap-4 px-6 pb-1 pt-0 overflow-x-auto scrollbar-hide",
                displayLeagues.length <= 3 ? "justify-center" : "justify-start"
            )}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
            {displayLeagues.map((league) => {
                const isCurrent = league.rank_level === currentRankLevel;
                const isUnlocked = league.rank_level <= currentRankLevel;

                return (
                    <div
                        key={league.league_id}
                        ref={isCurrent ? currentLeagueRef : null}
                        className={cn(
                            "relative flex-shrink-0 flex flex-col items-center transition-all duration-300",
                            isCurrent ? "scale-100 z-10" : isUnlocked ? "scale-75 opacity-80" : "scale-75 opacity-50 grayscale-[0.5]"
                        )}
                    >
                        {/* Icon Container - square aspect ratio */}
                        <div className={cn(
                            "w-27 h-28 relative flex items-center justify-center",
                            isCurrent ? "drop-shadow-2xl" : "drop-shadow-md"
                        )}>
                            {isUnlocked ? (
                                <LeagueIcon
                                    icon={league.icon}
                                    rankLevel={league.rank_level}
                                    name={league.name}
                                    className="w-full h-full text-6xl scale-[1.6]"
                                />
                            ) : (
                                <img 
                                    src={LOCKED_ICON} 
                                    alt="قفل" 
                                    className="w-full h-full object-contain filter drop-shadow-lg scale-[1.6]"
                                />
                            )}
                        </div>

                        {/* League Name */}
                        <div className={cn(
                            "-mt-2 text-center font-persian font-bold text-base transition-colors",
                            isCurrent ? "text-white" : "text-white/50"
                        )}>
                            {league.name}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
